import React, { FC, useState } from 'react';
import {
    List,
    Datagrid,
    TextField,
    DateField,
    TextInput,
    ListProps,
    FunctionField,
    useRecordContext,
    TopToolbar,
    FilterButton,
    ExportButton,
    useUpdate,
    useNotify,
    useRefresh,
} from 'react-admin';
import { Switch, FormControlLabel, Link as MuiLink } from '@mui/material';
import { Link, useNavigate } from 'react-router-dom';
import useAuthorize from '../hooks/useAuthorize';
import { UserRole } from '../types/types';

const CustomerFilter = [
    <TextInput label="Customer" source="customer_search" resettable alwaysOn />, 
];

const CUSTOMER_EXPORT_MAX_RESULTS = 10000;

const CustomerListActions: FC = () => (
    <TopToolbar>
        <FilterButton />
        <ExportButton maxResults={CUSTOMER_EXPORT_MAX_RESULTS} />
    </TopToolbar>
);

type WhatsAppFlagSource = 'whatsapp_alert_marked_premium' | 'whatsapp_alert_subscribed';

const WhatsAppFlagToggle: FC<{ source: WhatsAppFlagSource; label: string }> = ({ source, label }) => {
    const record = useRecordContext<Record<string, unknown>>();
    const [update, { isLoading }] = useUpdate();
    const notify = useNotify();
    const refresh = useRefresh();

    if (!record) return null;

    const checked = Boolean(record[source]);

    const handleChange = async (_event: React.ChangeEvent<HTMLInputElement>, nextChecked: boolean) => {
        try {
            await update('customers', {
                id: record.id,
                data: { ...record, [source]: nextChecked },
                previousData: record,
            });
            refresh();
        } catch (error: any) {
            notify(error?.message || `Failed to update ${label}`, { type: 'error' });
        }
    };

    return (
        <FormControlLabel
            control={
                <Switch
                    size="small"
                    checked={checked}
                    disabled={isLoading}
                    onChange={handleChange}
                    inputProps={{ 'aria-label': label }}
                />
            }
            label={checked ? 'Yes' : 'No'}
        />
    );
};

const UsernameLinkField: FC = () => {
    const record = useRecordContext<{ id: number; customer_username?: string }>();
    if (!record) return null;

    return (
        <Link to={`/customers/${record.id}`} style={{ textDecoration: 'none' }}>
            <MuiLink component="span" underline="hover">
                {record.customer_username || '—'}
            </MuiLink>
        </Link>
    );
};

const EngagementScoreField: FC = () => {
    const record = useRecordContext<{ whatsapp_alert_engagement_score?: number | null }>();
    if (!record) return null;

    const score = record.whatsapp_alert_engagement_score;
    if (score === null || score === undefined) {
        return <span style={{ color: '#999' }}>—</span>;
    }

    return <span>{Number(score)}</span>;
};

const BILLING_SKU_PRICE_ALERTS = 'buyer.price_alerts';

const SendLinkField: FC = () => {
    const record = useRecordContext<{ id: number }>();
    const notify = useNotify();
    const [loading, setLoading] = useState(false);

    if (!record) return null;

    const handleCreateLink = async (event: React.MouseEvent) => {
        event.preventDefault();
        if (loading) return;

        const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');
        const token = localStorage.getItem('user_authToken');
        if (!apiBase || !token) {
            notify('Sign in again to create a payment link', { type: 'warning' });
            return;
        }

        setLoading(true);
        try {
            const response = await fetch(`${apiBase}/billing/sessions`, {
                method: 'POST',
                headers: {
                    Accept: 'application/json',
                    'Content-Type': 'application/json',
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify({
                    customer_id: record.id,
                    sku_code: BILLING_SKU_PRICE_ALERTS,
                }),
            });
            const data = await response.json().catch(() => ({}));
            if (!response.ok) {
                throw new Error(data.error || 'Failed to create payment link');
            }
            const payUrl = data.pay_url as string;
            if (!payUrl) {
                throw new Error('No payment URL returned');
            }
            if (navigator.clipboard?.writeText) {
                await navigator.clipboard.writeText(payUrl);
                notify('Payment link copied to clipboard', { type: 'success' });
            } else {
                notify(`Payment link: ${payUrl}`, { type: 'info', autoHideDuration: 10000 });
            }
        } catch (error: unknown) {
            const message = error instanceof Error ? error.message : 'Failed to create payment link';
            notify(message, { type: 'error' });
        } finally {
            setLoading(false);
        }
    };

    return (
        <MuiLink
            href="#"
            underline="hover"
            onClick={handleCreateLink}
            sx={{ cursor: loading ? 'wait' : 'pointer', opacity: loading ? 0.6 : 1 }}
        >
            {loading ? '…' : 'Send'}
        </MuiLink>
    );
};

export const CustomerList: FC<ListProps> = props => {
    const navigate = useNavigate();
    useAuthorize([UserRole.Admin, UserRole.ProductAdmin]);

    const FavoriteLinkField: FC = () => {
        const record = useRecordContext<{ id: number; favorite_count: number }>();
        if (!record) return null;
        return (
            <MuiLink
                component="button"
                type="button"
                underline="hover"
                onClick={() => {
                    navigate(
                        `/customerfavorite?filter=${encodeURIComponent(
                            JSON.stringify({ customer_id: record.id })
                        )}`
                    );
                }}
            >
                {record.favorite_count ?? 0}
            </MuiLink>
        );
    };

    return (
        <List
            filters={CustomerFilter}
            hasCreate={false}
            actions={<CustomerListActions />}
            sort={{ field: 'whatsapp_alert_engagement_score', order: 'DESC' }}
            storeKey="customers.cre56"
            {...props}
        >
            <Datagrid bulkActionButtons={false}>
                <FunctionField
                    label="User Name"
                    sortBy="customer_username"
                    sortable
                    render={() => <UsernameLinkField />}
                />
                <TextField source="phone_mobile" label="Mobile Number" />
                <TextField source="company_name" label="Company Name" />
                <FunctionField
                    label="GSTs"
                    render={(record: { gst_count?: number }) => {
                        const count = Number(record?.gst_count ?? 0);
                        return count === 0 ? '0' : String(count);
                    }}
                />
                <FunctionField
                    label="Engagement Score"
                    sortBy="whatsapp_alert_engagement_score"
                    sortable
                    render={() => <EngagementScoreField />}
                />
                <FunctionField
                    label="Premium"
                    sortBy="whatsapp_alert_marked_premium"
                    sortable
                    render={() => (
                        <WhatsAppFlagToggle
                            source="whatsapp_alert_marked_premium"
                            label="Premium"
                        />
                    )}
                />
                <FunctionField
                    label="Subscribe"
                    sortBy="whatsapp_alert_subscribed"
                    sortable
                    render={() => (
                        <WhatsAppFlagToggle
                            source="whatsapp_alert_subscribed"
                            label="Subscribe"
                        />
                    )}
                />
                <FunctionField
                    label="Send"
                    render={() => <SendLinkField />}
                />
                <TextField source="customer_business_type" label="Business Type" />
                <DateField source="dt_created" label="Create Date" showTime />
                <DateField source="last_login" label="Last Login Date" showTime />
                <FunctionField
                    label="# Favorites"
                    render={() => <FavoriteLinkField />}
                    sortBy="favorite_count"
                    sortable
                />
            </Datagrid>
        </List>
    );
};

export default CustomerList;
